import * as React from "react";
import { cn } from "@/lib/utils";

export interface PictureProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  alt: string;
  avifSrc?: string;
  webpSrc?: string;
  fallbackSrc?: string;
  width?: number;
  height?: number;
  className?: string;
  imgClassName?: string;
}

/**
 * Picture component that uses modern image formats with proper fallbacks
 * and accessibility attributes using the HTML picture element.
 */
const Picture = React.forwardRef<HTMLDivElement, PictureProps>(
  ({ 
    src, 
    alt, 
    avifSrc, 
    webpSrc, 
    fallbackSrc,
    width, 
    height, 
    className, 
    imgClassName,
    ...props 
  }, ref) => {
    const isLocalImage = !src.startsWith('http') && !src.startsWith('//');
    
    // For local images, we can use vite-imagetools
    const optimizedAvifSrc = isLocalImage 
      ? `${src}?format=avif&quality=80` 
      : avifSrc || src;
      
    const optimizedWebpSrc = isLocalImage 
      ? `${src}?format=webp&quality=80` 
      : webpSrc || src;
    
    const optimizedFallbackSrc = fallbackSrc || src;

    return (
      <div 
        ref={ref} 
        className={cn("relative overflow-hidden", className)}
        style={{ width: width ? `${width}px` : 'auto', height: height ? `${height}px` : 'auto' }}
      >
        <picture>
          <source srcSet={optimizedAvifSrc} type="image/avif" />
          <source srcSet={optimizedWebpSrc} type="image/webp" />
          <img
            src={optimizedFallbackSrc}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
            className={cn("w-full h-full object-cover", imgClassName)}
            {...props}
          />
        </picture>
      </div>
    );
  }
);

Picture.displayName = "Picture";

export { Picture };